import { fetchFuturesMarkets, futuresMarketName, futuresMarketSearch, type FuturesMarket } from "./futures-market";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, Star, X } from "lucide-react";

import { fetchMarketSymbols } from "./api";
import type { Locale } from "./i18n";
import { marketDiscoveryT } from "./market-discovery-i18n";
import { MarketReferenceCache } from "./market-discovery-reference.ts";
import { referenceDate, referenceT } from "./public-reference-i18n.ts";
import {
  groupRemoteMarketSymbols,
  isLiveMarketSymbol,
  marketSymbolGroups,
  marketSymbolLabels,
  normalizeMarketDiscoveryProfile,
  recordRecentSymbol,
  searchLocalMarketSymbols,
  toggleFavoriteSymbol,
  type MarketDiscoveryProfile,
  type MarketSymbolGroup,
} from "./market-discovery";

const STORAGE_PREFIX = "backtest-market-discovery-v1";
const referenceCache = new MarketReferenceCache();

export function MarketDiscoverySelect({
  label,
  locale,
  scope,
  value,
  onChange,
  futuresOnly = false,
  provider,
  onSelectMarket,
}: {
  label: string;
  locale: Locale;
  scope: string;
  value: string;
  onChange: (value: string) => void;
  futuresOnly?: boolean;
  provider?: string;
  onSelectMarket?: (market: FuturesMarket) => void;
}) {
  const strings = marketDiscoveryT(locale);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const storageKey = `${STORAGE_PREFIX}:${encodeURIComponent(scope)}`;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [futuresMarkets, setFuturesMarkets] = useState<FuturesMarket[]>([]);
  const [remoteGroups, setRemoteGroups] = useState<MarketSymbolGroup[] | null>(null);
  const [reference, setReference] = useState<{ savedAt: number; cached: boolean }>();
  const [refreshing, setRefreshing] = useState(false);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine);
  const [profile, setProfile] = useState<MarketDiscoveryProfile>(() => loadProfile(storageKey));

  useEffect(() => {
    setProfile(loadProfile(storageKey));
  }, [storageKey]);

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setQuery("");
      }
    };
    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (futuresOnly) return;
    if (!isOpen) return;
    let cancelled = false;
    const cached = referenceCache.read(query, locale);
    setRemoteGroups(cached ? groupRemoteMarketSymbols(cached.value.items) : null);
    setReference(cached ? { savedAt: cached.savedAt, cached: true } : undefined);
    setRefreshFailed(false);
    setRefreshing(online);
    if (!online) return;
    const timer = window.setTimeout(() => {
      void fetchMarketSymbols(query, locale)
        .then((response) => {
          if (cancelled) return;
          const parsed = referenceCache.save(query, locale, response);
          setRemoteGroups(groupRemoteMarketSymbols(parsed.items));
          setReference({ savedAt: Date.now(), cached: false });
        })
        .catch(() => {
          if (!cancelled) setRefreshFailed(true);
        })
        .finally(() => { if (!cancelled) setRefreshing(false); });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isOpen, locale, online, query, futuresOnly]);

  useEffect(() => {
    if (!futuresOnly || !online) return;
    const controller = new AbortController();
    setRefreshing(true); setRefreshFailed(false);
    void fetchFuturesMarkets(controller.signal).then((catalog) => {
      if (!controller.signal.aborted) { setFuturesMarkets(catalog.items); setReference({ savedAt: Date.now(), cached: false }); }
    }).catch(() => { if (!controller.signal.aborted) setRefreshFailed(true); })
      .finally(() => { if (!controller.signal.aborted) setRefreshing(false); });
    return () => controller.abort();
  }, [futuresOnly, online]);

  const localGroups = useMemo(() => searchLocalMarketSymbols(query), [query]);
  const resultGroups = futuresOnly ? groupRemoteMarketSymbols(futuresMarketSearch(futuresMarkets, query).map((market) => ({ symbol: market.backtest_defaults.symbol, displayName: `${futuresMarketName(market, locale)} · ${market.display_symbol}`, group: market.group, assetClass: market.asset_class }))) : remoteGroups ?? localGroups;
  const selectedFuturesMarket = futuresMarkets.find((market) => market.backtest_defaults.symbol === value && market.source_provider === provider);
  const option = (symbol: string): [string, string] | undefined => {
    if (!futuresOnly) return symbolOption(symbol) ?? undefined;
    const market = futuresMarkets.find((market) => market.backtest_defaults.symbol === symbol);
    return market ? [symbol, `${futuresMarketName(market, locale)} · ${market.display_symbol}`] : undefined;
  };
  const favorites = useMemo(
    () => profile.favoriteSymbols.map(option).filter(Boolean) as [string, string][],
    [profile.favoriteSymbols, futuresMarkets, locale, futuresOnly],
  );
  const recents = useMemo(
    () => profile.recentSymbols.map(option).filter(Boolean) as [string, string][],
    [profile.recentSymbols, futuresMarkets, locale, futuresOnly],
  );

  const storeProfile = (next: MarketDiscoveryProfile) => {
    setProfile(next);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // Private browsing or a storage quota can disable persistence without
      // blocking market selection.
    }
  };

  const chooseSymbol = (symbol: string) => {
    if (!available(symbol)) return;
    const market = futuresOnly ? futuresMarkets.find((item) => item.backtest_defaults.symbol === symbol) : undefined;
    if (market && onSelectMarket) onSelectMarket(market); else onChange(symbol);
    storeProfile(recordRecentSymbol(profile, symbol));
    setIsOpen(false);
    setQuery("");
  };

  const toggleFavorite = (symbol: string) => {
    storeProfile(toggleFavoriteSymbol(profile, symbol));
  };

  const available = (symbol: string) => futuresOnly
    ? futuresMarkets.some((market) => market.backtest_defaults.symbol === symbol)
    : isLiveMarketSymbol(symbol);

  return (
    <div className="field market-discovery" ref={rootRef}>
      <span>{label}</span>
      <button
        type="button"
        className="market-discovery-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
      >
        <span>
          <strong>{selectedFuturesMarket ? `${futuresMarketName(selectedFuturesMarket, locale)} · ${selectedFuturesMarket.display_symbol}` : value}</strong>
          <small>{selectedFuturesMarket ? `${selectedFuturesMarket.source_provider === "binance_futures" ? "Binance Futures" : "Yahoo Finance"} · ${selectedFuturesMarket.source_symbol}` : marketSymbolLabels[value] || strings.selectMarket}</small>
        </span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="market-discovery-popover">
          <div className="market-discovery-header">
            <strong>{strings.selectMarket}</strong>
            <button type="button" aria-label={strings.close} onClick={() => setIsOpen(false)}>
              <X size={16} aria-hidden="true" />
            </button>
          </div>
          <label className="market-discovery-search">
            <Search size={16} aria-hidden="true" />
            <input
              ref={inputRef}
              value={query}
              placeholder={strings.searchMarkets}
              aria-label={strings.searchMarkets}
              maxLength={64}
              onChange={(event) => {
                setQuery(event.target.value);
                setRemoteGroups(null);
                setReference(undefined);
              }}
            />
          </label>
          <p className="market-discovery-empty" role="status" style={{ margin: "8px 12px", textAlign: "start" }}>
            {reference
              ? referenceT(locale, reference.cached ? "cached" : "updated", { date: referenceDate(locale, reference.savedAt) })
              : referenceT(locale, "bundled")}
            {!online && ` · ${referenceT(locale, "offline")}`}
            {online && refreshing && ` · ${referenceT(locale, "refreshing")}`}
            {online && refreshFailed && ` · ${referenceT(locale, "refreshFailed")}`}
          </p>
          <div className="market-discovery-results" role="listbox" aria-label={label}>
            {!query && favorites.length > 0 && (
              <MarketSection
                name={strings.favorites}
                symbols={favorites}
                value={value}
                favorites={profile.favoriteSymbols}
                strings={strings}
                onChoose={chooseSymbol}
                onToggleFavorite={toggleFavorite}
                isAvailable={available}
              />
            )}
            {!query && recents.length > 0 && (
              <MarketSection
                name={strings.recent}
                symbols={recents}
                value={value}
                favorites={profile.favoriteSymbols}
                strings={strings}
                onChoose={chooseSymbol}
                onToggleFavorite={toggleFavorite}
                isAvailable={available}
              />
            )}
            {resultGroups.map((group) => (
              <MarketSection
                key={group.name}
                name={group.name}
                symbols={group.symbols}
                value={value}
                favorites={profile.favoriteSymbols}
                strings={strings}
                onChoose={chooseSymbol}
                onToggleFavorite={toggleFavorite}
                isAvailable={available}
              />
            ))}
            {resultGroups.length === 0 && (
              <p className="market-discovery-empty">{strings.noMatches}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function MarketSection({
  name,
  symbols,
  value,
  favorites,
  strings,
  onChoose,
  onToggleFavorite,
  isAvailable = isLiveMarketSymbol,
}: {
  name: string;
  symbols: [string, string][];
  value: string;
  favorites: string[];
  strings: ReturnType<typeof marketDiscoveryT>;
  onChoose: (symbol: string) => void;
  onToggleFavorite: (symbol: string) => void;
  isAvailable?: (symbol: string) => boolean;
}) {
  return (
    <section className="market-discovery-section">
      <p>{name}</p>
      {symbols.map(([symbol, displayName]) => {
        const isFavorite = favorites.includes(symbol);
        return (
          <div className="market-discovery-row" key={`${name}-${symbol}`}>
            <button
              type="button"
              role="option"
              aria-selected={value === symbol}
              disabled={!isAvailable(symbol)}
              onClick={() => onChoose(symbol)}
            >
              <span><strong>{symbol}</strong><small>{displayName}</small></span>
              {value === symbol && <Check size={16} aria-hidden="true" />}
            </button>
            <button
              type="button"
              className={isFavorite ? "favorite active" : "favorite"}
              aria-label={isFavorite ? strings.removeFavorite : strings.addFavorite}
              disabled={!isAvailable(symbol) && !isFavorite}
              onClick={() => onToggleFavorite(symbol)}
            >
              <Star size={15} fill={isFavorite ? "currentColor" : "none"} aria-hidden="true" />
            </button>
          </div>
        );
      })}
    </section>
  );
}

function symbolOption(symbol: string): [string, string] | null {
  const label = marketSymbolLabels[symbol];
  return label ? [symbol, label] : null;
}

function loadProfile(storageKey: string): MarketDiscoveryProfile {
  try {
    return normalizeMarketDiscoveryProfile(JSON.parse(window.localStorage.getItem(storageKey) || "null"));
  } catch {
    return normalizeMarketDiscoveryProfile(null);
  }
}
